import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IOptionItem, SelectComponent } from '@ui-lib/components';
import {
  debounceTime,
  map,
  Observable,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { ServiceCenterDictionaryService } from '../service-center-selector/service-center-dictionary.service';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { LoadingState } from '../../types/loading.type';

@Component({
  selector: 'mon-service-center-select',
  templateUrl: './service-center-select.component.html',
  styleUrl: './service-center-select.component.scss',
  imports: [ReactiveFormsModule, SelectComponent, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ServiceCenterDictionaryService],
})
export class ServiceCenterSelectComponent {
  protected readonly serviceCenterDictionaryService = inject(ServiceCenterDictionaryService);
  private readonly keyword$ = new Subject<string>();

  @Input()
  public controlEntity: FormControl<IOptionItem<string> | null> = new FormControl(null);

  @Input()
  public labelTranslateKey = 'monitoring.service-center';

  @Input()
  public searchTranslateKey = 'monitoring.search';

  @Input()
  public fullWidth = false;

  @Input()
  public isClearable = false;

  protected readonly serviceCenterOptions$: Observable<IOptionItem<string>[]> = this.keyword$.pipe(
    startWith(''),
    debounceTime(500),
    switchMap(() =>
      this.serviceCenterDictionaryService.source$.pipe(
        scan(
          (
            acc: IDictionaryItem<string>[],
            newbies: IDictionaryItem<string>[],
          ): IDictionaryItem<string>[] => [...acc, ...newbies],
          [],
        ),
        map((items: IDictionaryItem<string>[]): IOptionItem<string>[] =>
          items.map((item): IOptionItem<string> => ({ id: item.id, label: item.name })),
        ),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly LoadingState = LoadingState;

  public loadMore(): void {
    this.serviceCenterDictionaryService.loadMore();
  }

  public onKeywordChange(keyword: string): void {
    this.serviceCenterDictionaryService.updateKeyword(keyword);
    this.keyword$.next(keyword);
  }
}
